/**
 * MoonPhaseShare — MP4 screen of the Moon Phase Soulmate flow.
 *
 * Route: /flow/moon-phase-soulmate/share
 *
 * T23 implementation:
 *   - Off-screen full-size ShareCard (1080×1920) targeted by html-to-image.
 *   - On-screen scaled-down preview (transform: scale(0.25) at 270×480).
 *   - Share CTA captures → tries Telegram shareToStory → falls back to download.
 *   - Re-cast affordance loops back through /flow/moon-phase-soulmate/computing.
 *   - Missing location.state → redirect to /flow/moon-phase-soulmate/computing.
 *   - Done → /catalog.
 */

import { useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SKU_CATALOG } from '@wds/astro-bot-shared/catalog';
import { CosmosBackdrop } from '../../brand/CosmosBackdrop';
import { ShareCard } from './ShareCard';
import { captureShareCardJpeg } from '../../../lib/share-card';
import { shareMoonPhaseToChat } from '../../../lib/moon-share';
import { useAuth } from '../../../store/auth.js';
import type { MoonPhaseOutput } from '../../../hooks/useRunMoonPhase';
import styles from './MoonPhase.module.css';

// --------------------------------------------------------------------------
// Share status
// --------------------------------------------------------------------------

type ShareStatus =
  | 'idle'
  | 'capturing'
  | 'sent'        // Telegram story dispatched
  | 'downloaded'  // fallback download triggered
  | 'error';

// --------------------------------------------------------------------------
// Main screen
// --------------------------------------------------------------------------

export function MoonPhaseShare(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('flows');
  const locationState = location.state as { output?: MoonPhaseOutput } | null;
  const output = locationState?.output ?? null;

  // Redirect guard — no state means deep-link without output
  useEffect(() => {
    if (!output) {
      void navigate('/flow/moon-phase-soulmate/computing', { replace: true });
    }
  }, [output, navigate]);

  const cardRef = useRef<HTMLDivElement>(null);
  const jwt = useAuth((s) => s.jwt);
  const [status, setStatus] = useState<ShareStatus>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // -----------------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------------

  async function handleShare() {
    if (!cardRef.current) return;

    setStatus('capturing');
    setErrorMsg(null);

    try {
      // Capture the personalized card (JPEG) and share it to a chosen chat:
      // prepared inline message on TG 8.0+, else a link share. Both open the
      // native chat/contact picker.
      const jpeg = await captureShareCardJpeg(cardRef.current);
      await shareMoonPhaseToChat(jpeg, jwt, t('moonPhase.share.freeAstrology'));
      setStatus('sent');
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('moonPhase.share.captureFailed');
      setErrorMsg(msg);
      setStatus('error');
    }
  }

  function handleRecast() {
    void navigate('/flow/moon-phase-soulmate/computing');
  }

  function handleDone() {
    void navigate('/catalog');
  }

  // -----------------------------------------------------------------------
  // Before output is available (redirect in progress) — render nothing
  // -----------------------------------------------------------------------

  if (!output) {
    return <div className={styles.root} />;
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  const isCapturing = status === 'capturing';

  return (
    <div className={styles.root}>
      <CosmosBackdrop />

      {/* Off-screen full-size card for capture (1080×1920) */}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          left: '-99999px',
          top: 0,
          width: '1080px',
          height: '1920px',
          pointerEvents: 'none',
          zIndex: -1,
        }}
      >
        <ShareCard ref={cardRef} output={output} />
      </div>

      <main
        role="main"
        aria-label={t('moonPhase.share.mainAria')}
        className={styles.content}
      >
        {/* Header */}
        <div className={styles.headerRow}>
          <div style={{ width: 44 }} aria-hidden="true" />
          <span className={styles.eyebrow} data-testid="mp-share-eyebrow">
            {t('moonPhase.share.eyebrow')}
          </span>
          <div style={{ width: 44 }} aria-hidden="true" />
        </div>

        {/* On-screen scaled preview (270×480 = 1080×1920 × 0.25) */}
        <div
          data-testid="mp-share-card-preview"
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
          }}
        >
          <div
            style={{
              width: 270,
              height: 480,
              overflow: 'hidden',
              borderRadius: 12,
              boxShadow: '0 18px 50px rgba(0,0,0,0.6), 0 0 0 0.5px rgba(212,175,55,0.4)',
              position: 'relative',
              flexShrink: 0,
            }}
          >
            <div
              aria-hidden="true"
              style={{
                transformOrigin: 'top left',
                transform: 'scale(0.25)',
                width: 1080,
                height: 1920,
                pointerEvents: 'none',
              }}
            >
              <ShareCard output={output} />
            </div>
          </div>
        </div>

        {/* CTA area */}
        <div className={styles.ctaArea}>
          {/* Status feedback */}
          {status === 'sent' && (
            <p
              className={styles.disabledCaption}
              data-testid="mp-share-status-sent"
              style={{ color: '#7ed3a3' }}
            >
              {t('moonPhase.share.statusSent')}
            </p>
          )}

          {status === 'downloaded' && (
            <p
              className={styles.disabledCaption}
              data-testid="mp-share-status-downloaded"
            >
              {t('moonPhase.share.statusDownloaded')}
            </p>
          )}

          {status === 'error' && (
            <p
              className={styles.disabledCaption}
              data-testid="mp-share-status-error"
              style={{ color: '#b86b28' }}
            >
              {errorMsg ?? t('moonPhase.share.statusErrorFallback')}
            </p>
          )}

          {/* Primary CTA */}
          <button
            type="button"
            className={styles.ctaPrimary}
            onClick={() => { void handleShare(); }}
            disabled={isCapturing}
            aria-disabled={isCapturing}
            data-testid="mp-share-generate-btn"
          >
            {isCapturing ? t('moonPhase.share.ctaCapturing') : t('moonPhase.share.cta')}
          </button>

          {/* Re-cast */}
          <button
            type="button"
            className={styles.ctaSecondary}
            onClick={handleRecast}
            data-testid="mp-share-recast-btn"
          >
            {t('moonPhase.share.recastCta')}
          </button>

          {/* Done */}
          <button
            type="button"
            className={styles.ctaSecondary}
            onClick={handleDone}
            data-testid="mp-share-done-btn"
          >
            {t('moonPhase.share.doneCta')}
          </button>

          {/* Compat upsell tile */}
          <div
            className={styles.upsellTile}
            data-testid="mp-upsell-tile"
            role="button"
            tabIndex={0}
            onClick={() => void navigate('/sku/compat_check')}
            onKeyDown={(e) => { if (e.key === 'Enter') void navigate('/sku/compat_check'); }}
          >
            <p className={styles.upsellHeading}>{t('moonPhase.share.upsellHeading')}</p>
            <p className={styles.upsellSub}>{t('moonPhase.share.upsellSub')}</p>
            <span className={styles.upsellPrice}>{t('moonPhase.share.upsellPrice', { stars: SKU_CATALOG['compat_check'].stars })}</span>
          </div>
        </div>
      </main>
    </div>
  );
}
